import React, { useState } from "react";
import { X, Search } from "lucide-react";
import Card from "./card";
import Badge from "./badge";

const ReferenceMenuPanel = ({ isOpen, onClose, menuData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("None");

  // Transform wine data
  const wineData = Array.isArray(menuData)
    ? menuData.map((wine) => {
      return {
        name: wine?.product_name,
        producer: wine?.producer_name,
        year: wine?.vintage?.toString(),
        varietals: wine?.varietals?.join(", "),
        region: `${wine?.region?.country} → ${wine?.region?.region || "N/A"} → ${wine?.region?.sub_region || wine?.region?.commune_appellation || "N/A"}`,
        regionData: {
          country: wine?.region?.country || "",
          region: wine?.region?.region || "",
          sub: wine?.region?.sub_region || wine?.region?.commune_appellation || ""
        },
        category: wine?.category,
        style: wine?.sub_category,
        body: wine?.style?.body || "N/A",
        image_url: wine?.image_url || "N/A",
        texture: wine?.style?.texture || "N/A",
        flavorIntensity: wine?.style?.flavor_intensity || "N/A",
        notes: wine?.notes,
        price: {
          glass: wine?.offering?.glass_price,
          bottle: wine?.offering?.bottle_price,
        }, // Add price if available in your data
        tags: [
          ...(wine?.is_organic ? ["Organic"] : []),
          ...(wine?.is_biodynamic ? ["Biodynamic"] : []),
          ...(wine?.is_vegan ? ["Vegan"] : []),
          ...(wine?.is_filtered ? ["Filtered"] : []),
          ...(wine?.has_residual_sugar ? ["Residual Sugar"] : []),
        ],
      };
    })
    : [];
  const wineCategories = [
    "white",
    "red",
    "rose",
    "orange",
    "sparkling",
    "dessert",
  ];

  const handleOptionChange = async (e) => {
    const { value } = e.target;
    setFilterType(value);
  };

  // Update your filteredWineData to include the filter logic
  const filteredWineData = wineData.filter((wine) => {
    // First apply search filter
    const matchesSearch =
      wine?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wine?.producer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wine?.varietals?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wine?.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wine?.style?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wine?.tags?.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    // Then apply price filter
    let matchesFilter = true;
    if (filterType === "by_the_glass") {
      matchesFilter = wine?.price?.glass && wine.price.glass > 0;
    } else if (filterType === "by_the_bottle") {
      matchesFilter = wine?.price?.bottle && wine.price.bottle > 0;
    }
    // If filterType is "None", matchesFilter remains true

    return matchesSearch && matchesFilter;
  });

  const winesByCategory = wineCategories.reduce((acc, category) => {
    acc[category] = filteredWineData.filter(
      (wine) => wine.category && wine.category.toLowerCase() === category
    );
    return acc;
  }, {});

  return (
    <div
      className={`fixed inset-y-0 right-0 w-full max-w-sm bg-[#FFF9F3] shadow-lg z-[102] transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
        }`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="text-lg font-bold text-black">Reference Menu</h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-red-500 p-1 rounded-full"
        >
          <X size={22} />
        </button>
      </div>
      <div className="p-4 overflow-y-auto h-[calc(100%-64px)]">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search menu items..."
              className="w-full pl-10 pr-4 py-2 rounded-md bg-background border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#e11d48] text-black text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="by_the_filter"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Filter Options
          </label>
          <div className="relative">
            <select
              id="by_the_filter"
              name="by_the_filter"
              className="w-full pl-4 pr-10 py-2 rounded-md bg-background border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#e11d48] text-black text-base appearance-none cursor-pointer"
              onChange={handleOptionChange}
            >
              <option value="None">None</option>
              <option value="by_the_glass">By The Glass</option>
              <option value="by_the_bottle">By The Bottle</option>
            </select>
            {/* Custom dropdown arrow */}
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Render each wine category section dynamically */}
        {wineCategories.map(
          (category) =>
            winesByCategory[category].length > 0 && (
              <div className="mb-6" key={category}>
                <h3 className="text-md text-left font-semibold text-black mb-3">
                  {category.charAt(0).toUpperCase() + category.slice(1)} Wines
                </h3>
                <div className="space-y-4">
                  {winesByCategory[category].map((wine) => {
                    const getStyleBadgeColor = (cat) => {
                      const categoryLower = cat?.toLowerCase() || "";
                      if (categoryLower === "red") return "bg-red-50 text-red-700";
                      if (categoryLower === "white") return "bg-amber-50 text-amber-700";
                      if (categoryLower === "sparkling") return "bg-yellow-50 text-yellow-700";
                      if (categoryLower === "rosé" || categoryLower === "rose") return "bg-pink-50 text-pink-700";
                      if (categoryLower === "orange") return "bg-orange-50 text-orange-700";
                      if (categoryLower === "dessert") return "bg-amber-50 text-amber-700";
                      return "bg-gray-50 text-gray-700";
                    };

                    const categoryBadgeColor = () => {
                      const categoryLower = wine.category?.toLowerCase() || "";
                      if (categoryLower === "red") return "bg-[#9F1239] text-white border-transparent";
                      if (categoryLower === "white") return "bg-[#EAB308] text-white border-transparent";
                      if (categoryLower === "rosé" || categoryLower === "rose") return "bg-[#F472B6] text-white border-transparent";
                      return "bg-gray-800 text-white border-transparent";
                    };

                    return (
                      <div
                        key={wine.name}
                        className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col h-full"
                      >
                        <div className="relative h-64 w-full flex-shrink-0 bg-white">
                          {wine.image_url && wine.image_url !== "N/A" ? (
                            <img
                              src={
                                wine.image_url.startsWith("http")
                                  ? wine.image_url
                                  : `${process.env.REACT_APP_IMAGE_BASE_URL}${wine.image_url.startsWith("/") ? "" : "/"
                                  }${wine.image_url}`
                              }
                              alt={wine.name}
                              referrerPolicy="no-referrer"
                              className="w-full h-full object-contain p-2"
                            />
                          ) : (
                            <div className="h-full w-full bg-gray-50 flex items-center justify-center text-gray-300">
                              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}

                          <div className="absolute top-3 right-3 flex gap-2">
                            {wine.year && wine.year !== "N/A" && (
                              <span className="px-2 py-1 bg-white shadow-sm rounded-lg text-[12px] font-bold text-gray-900 border border-gray-100">
                                {wine.year}
                              </span>
                            )}
                            {wine.category && (
                              <span className={`px-3 py-1 shadow-sm rounded-lg text-[12px] font-bold capitalize border ${categoryBadgeColor()}`}>
                                {wine.category}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="p-4 flex text-start flex-col flex-grow space-y-4 justify-between min-w-0 border-t border-gray-100">
                          <div className="space-y-4 min-w-0">

                            <div className="space-y-0.5 min-w-0 flex-1 border-b border-gray-100 pb-3">
                              <h3 className="text-base font-black text-gray-900 leading-tight break-words">
                                {wine.producer || "Unknown Producer"}
                              </h3>
                              <p className="text-sm font-bold text-gray-500 break-words">
                                {wine.name}
                              </p>
                            </div>

                            <div className="space-y-2 min-w-0 pt-1">
                              {/* Varietals */}
                              <div className="grid grid-cols-[75px_1fr] items-center sm:items-start gap-2">
                                <span className="text-[11px] sm:text-[12px] font-medium text-gray-500 py-0.5">
                                  Varietals:
                                </span>
                                <div className="flex flex-wrap gap-1 min-w-0">
                                  {wine.varietals && wine.varietals !== "N/A" ? wine.varietals.split(", ").map((v, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] sm:text-[12px] font-bold">{v}</span>
                                  )) : <span className="text-[10px] sm:text-[12px] text-gray-300 italic">N/A</span>}
                                </div>
                              </div>

                              {/* Region */}
                              <div className="grid grid-cols-[75px_1fr] items-center gap-2">
                                <span className="text-[11px] sm:text-[12px] font-medium text-[#486581]">
                                  Region:
                                </span>
                                <div className="text-[11px] sm:text-[12px] break-words flex items-center flex-wrap gap-1">
                                  {wine.regionData?.country && (
                                    <span className="text-[#102A43] font-bold">{wine.regionData.country}</span>
                                  )}
                                  {wine.regionData?.region && wine.regionData.region !== "N/A" && (
                                    <>
                                      <span className="text-gray-400 font-normal">›</span>
                                      <span className="text-[#627D98] font-bold">{wine.regionData.region}</span>
                                    </>
                                  )}
                                  {wine.regionData?.sub && wine.regionData.sub !== "N/A" && (
                                    <>
                                      <span className="text-gray-400 font-normal">›</span>
                                      <span className="text-[#829AB1] font-medium">{wine.regionData.sub}</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Style */}
                              <div className="grid grid-cols-[75px_1fr] items-center gap-2">
                                <span className="text-[11px] sm:text-[12px] font-medium text-gray-500">
                                  Style:
                                </span>
                                <div className="flex flex-wrap gap-1 min-w-0">
                                  {wine.style && wine.style !== "N/A" ? (
                                    <span className={`px-2 py-0.5 rounded text-[10px] sm:text-[12px] font-bold ${getStyleBadgeColor(wine.category)}`}>{wine.style}</span>
                                  ) : <span className="text-[10px] sm:text-[12px] text-gray-300 italic">N/A</span>}
                                </div>
                              </div>

                              {/* Body */}
                              <div className="grid grid-cols-[75px_1fr] items-center gap-2">
                                <span className="text-[11px] sm:text-[12px] font-medium text-gray-500">
                                  Body:
                                </span>
                                <div className="flex flex-wrap gap-1 min-w-0">
                                  {wine.body && wine.body !== "N/A" ? (
                                    <span className="px-2 py-0.5 bg-teal-50/50 text-teal-700 rounded text-[10px] sm:text-[12px] font-bold">{wine.body}</span>
                                  ) : <span className="text-[10px] sm:text-[12px] text-gray-300 italic">N/A</span>}
                                </div>
                              </div>

                              {/* Texture */}
                              <div className="grid grid-cols-[75px_1fr] items-center gap-2">
                                <span className="text-[11px] sm:text-[12px] font-medium text-gray-500">
                                  Texture:
                                </span>
                                <div className="flex flex-wrap gap-1 min-w-0">
                                  {wine.texture && wine.texture !== "N/A" ? (
                                    <span className="px-2 py-0.5 bg-teal-50/50 text-teal-700 rounded text-[10px] sm:text-[12px] font-bold">{wine.texture}</span>
                                  ) : <span className="text-[10px] sm:text-[12px] text-gray-300 italic">N/A</span>}
                                </div>
                              </div>

                              {/* Intensity */}
                              <div className="grid grid-cols-[75px_1fr] items-center gap-2">
                                <span className="text-[11px] sm:text-[12px] font-medium text-gray-500">
                                  Intensity:
                                </span>
                                <div className="flex flex-wrap gap-1 min-w-0">
                                  {wine.flavorIntensity && wine.flavorIntensity !== "N/A" ? (
                                    <span className="px-2 py-0.5 bg-teal-50/50 text-teal-700 rounded text-[10px] sm:text-[12px] font-bold">{wine.flavorIntensity}</span>
                                  ) : <span className="text-[10px] sm:text-[12px] text-gray-300 italic">N/A</span>}
                                </div>
                              </div>

                              {/* Notes */}
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="bg-[#FFFCF6] rounded-xl p-3 border border-orange-50">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <div className="w-2.5 h-2.5 bg-[#EAB308] rounded-sm"></div>
                                    <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                                      Notes
                                    </span>
                                  </div>
                                  <p className="text-[10px] sm:text-[12px] text-gray-600 font-medium italic break-words pl-4">
                                    {wine.notes || "None"}
                                  </p>
                                </div>
                              </div>

                              {/* Price */}
                              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-2">
                                {wine?.price?.glass > 0 && (
                                  <div className="flex items-baseline gap-2">
                                    <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px] sm:text-[11px] font-bold uppercase min-w-[36px] text-center">BTG</span>
                                    <span className="font-black text-gray-900 text-sm sm:text-base">
                                      {new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                        minimumFractionDigits: 0,
                                      }).format(wine.price.glass)}
                                    </span>
                                    <span className="text-gray-400 text-[10px] sm:text-[12px] font-medium -ml-1">/ glass</span>
                                  </div>
                                )}
                                {wine?.price?.bottle > 0 && (
                                  <div className="flex items-baseline gap-2">
                                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] sm:text-[11px] font-bold uppercase min-w-[36px] text-center">BTB</span>
                                    <span className="font-black text-gray-900 text-sm sm:text-base">
                                      {new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                        minimumFractionDigits: 0,
                                      }).format(wine.price.bottle)}
                                    </span>
                                    <span className="text-gray-400 text-[10px] sm:text-[12px] font-medium -ml-1">/ bottle</span>
                                  </div>
                                )}
                              </div>

                              {/* Tags */}
                              {wine.tags && wine.tags.length > 0 && (
                                <div className="mt-4 pt-3 flex flex-wrap gap-2">
                                  {wine.tags.map((tag) => {
                                    let badgeClass = "bg-gray-100 text-gray-600";
                                    if (tag === "Vegan") badgeClass = "bg-green-50 text-green-700";
                                    if (tag === "Organic" || tag === "Biodynamic") badgeClass = "bg-emerald-50 text-emerald-700";
                                    if (tag === "Residual Sugar") badgeClass = "bg-yellow-50 text-yellow-700";
                                    return (
                                      <span
                                        key={tag}
                                        className={`${badgeClass} text-[10px] sm:text-[11px] px-2.5 py-1 font-bold rounded-full`}
                                      >
                                        {tag}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
        )}

        {filteredWineData.length === 0 && (
          <div className="text-center text-gray-600 mt-8">
            {searchTerm
              ? `No results found for "${searchTerm}"`
              : "No wine items available"}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceMenuPanel;
